import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'server/trpc'
import * as v from 'valibot'
import { useSessionUser } from '@/hooks/use-session'
import { pickIncompleteTestPath } from '@/main-helpers'
import { ConnectedAccounts } from './-components/settings/connected-accounts'
import { DeleteAccount } from './-components/shared/delete-account'
import {
  SETTINGS_FIELDS,
  SETTINGS_STACK,
  SettingsSection,
  SettingsSectionSkeleton,
} from './-components/settings/settings-section'
import { sectionTone, type SectionTone } from './-components/shared/section-heading'
import { ProfilePending } from './-components/settings/account-pending'
import { useLingui } from '@lingui/react/macro'
import { Avatar } from './-components/shared/avatar'
import { fileToBase64 } from '@/components/custom/form'
import { useAppForm } from '@/lib/use-app-form'
import { noop, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import {
  AlertTriangleIcon,
  BriefcaseIcon,
  BuildingIcon,
  CameraIcon,
  Link2Icon,
  LockKeyholeIcon,
  PlugIcon,
  MailIcon,
  MapPinIcon,
  PhoneIcon,
  StarIcon,
  UserIcon,
  UserRoundIcon,
} from '@/routes/_app/-components/settings/settings-icons'
import { SparklesIcon } from '@/routes/_app/-components/shared/navigation-config'
import { useRef, useState, Suspense } from 'react'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { usePermissions } from '@/hooks/use-permissions'
import { FormReadOnlyProvider } from '@/components/custom/form/shared'
import { ImageEditor } from '@/components/custom/form/-components/image-editor'
import { Collapse } from '@/components/custom/collapse'
import { useLists } from '@/hooks/use-lists'
import { toast } from '@/lib/toast'
import { useAppStore } from '@/lib/app-store'
import { revalidateLogic } from '@tanstack/react-form'
import { birthdateSchemaKeeping, jobInfoSchema, personalInfoSchema } from 'shared/applicant-form'
import { ButtonPrimitive } from '@/components/ui/button-primitive'
import { Switch } from '@/components/ui/switch'

// Every section of the profile in Billing's hue, so the account's own pages read as one; a danger zone keeps its rose.
const PROFILE_TONE: SectionTone = sectionTone('amber')

type SessionUser = inferRouterOutputs<AppRouter>['auth']['getUser']

/**
 * Which body /account paints for a session, so the loader warms only the reads of the branch the component takes.
 * An admin/authorized employee acting AS the company gets the COMPANY settings; with a company as the active
 * employer an employee reads their profile as it stores it; with D'Anchiano active they edit their own data.
 */
function accountBody(user: SessionUser) {
  const isEmployee = user?.type === 'applicant' && user.isEmployee
  if (isEmployee && user.activeEmployer?.companyId != null) {
    return user.interfaceMode === 'company' ? 'actingCompany' : 'employee'
  }
  return user?.type ?? null
}

export const Route = createFileRoute('/_app/account/')({
  context: () => ({
    companyProfileQueryOptions: trpc.companies.getCompanyProfile.queryOptions(),
    myEmployeeProfileQueryOptions: trpc.employees.getMyEmployeeProfile.queryOptions(),
    myTeamsQueryOptions: trpc.employees.getMyTeams.queryOptions(),
    myAssignedJobsQueryOptions: trpc.employees.getMyAssignedJobs.queryOptions(),
    adminEmployeesQueryOptions: trpc.employees.getAdminEmployees.queryOptions(),
    mcpCompanyAccessQueryOptions: trpc.mcp.getCompanyAccess.queryOptions(),
  }),
  loader: ({ context }) => {
    // The session the root warmed, when already in hand. A document's first loader has none yet: its body then
    // reads its branch once the session resolves, which is when a chain on the session would have asked too.
    const user = context.queryClient.getQueryData(context.sessionUserQueryOptions.queryKey)
    if (user === undefined) return
    const body = accountBody(user)
    if (body === 'actingCompany') context.queryClient.query(context.companyProfileQueryOptions).catch(noop)
    if (body === 'employee') {
      context.queryClient.query(context.myEmployeeProfileQueryOptions).catch(noop)
      context.queryClient.query(context.myTeamsQueryOptions).catch(noop)
      context.queryClient.query(context.myAssignedJobsQueryOptions).catch(noop)
    }
    if (body === 'company') context.queryClient.query(context.adminEmployeesQueryOptions).catch(noop)
    if ((body === 'company' || body === 'actingCompany') && user?.assistantEnabled) {
      context.queryClient.query(context.mcpCompanyAccessQueryOptions).catch(noop)
    }
  },
  component: RouteComponent,
  pendingComponent: ProfilePending,
})

function RouteComponent() {
  const body = accountBody(useSessionUser())
  if (body === 'actingCompany') return <CompanyProfileAsActing />
  if (body === 'employee') return <EmployeeProfileReadOnly />
  if (body === 'applicant') return <UpdateApplicant />
  if (body === 'company') return <UpdateCompany />
  return null
}

// Company settings ("Configuración") for an admin/authorized employee acting AS the company (Block C).
// Bounded to Company Information + Contact — the core company config — sourced from and written to the
// ACTING company (server-resolved, IDOR-safe). Editable by admins (employees:write); read-only for
// authorized (the whole form is wrapped in FormReadOnlyProvider and the Save buttons are hidden).
function CompanyProfileAsActing() {
  const { t } = useLingui()
  const { can } = usePermissions()
  const canWrite = can('employees:write')
  const lists = useLists()
  const { data: company } = useSuspenseQuery(Route.useRouteContext().companyProfileQueryOptions)
  const { mutateAsync: updateCompanyProfile } = useMutation(trpc.companies.updateCompanyProfile.mutationOptions())

  const companyForm = useAppForm({
    validators: {
      onChange: v.object({
        name: v.pipe(v.string(), v.minLength(1)),
        country: v.pipe(v.string(), v.minLength(1)),
        province: v.pipe(v.string(), v.minLength(1)),
        city: v.pipe(v.string(), v.minLength(1)),
        cp: v.string(),
        employees: v.nullable(v.number()),
        sector: v.nullable(v.number()),
      }),
    },
    defaultValues: {
      // A freshly registered company keeps its name in the `company` column; UpdateCompany moves it to
      // `name` (firstname) on the first save. Coalesce so the acting admin always sees the real name.
      name: company?.name ?? company?.company ?? '',
      country: company?.country ?? '',
      province: company?.province ?? '',
      city: company?.city ?? '',
      cp: company?.cp ?? '',
      employees: company?.employees ?? null,
      sector: company?.sector ?? null,
    },
    onSubmit: async ({ value }) => {
      await updateCompanyProfile(value)
      toast.success(t`Saved successfully`)
    },
  })

  const contactForm = useAppForm({
    validators: {
      onChange: v.object({
        contactFirstname: v.pipe(v.string(), v.minLength(1)),
        contactLastname: v.pipe(v.string(), v.minLength(1)),
        contactPhone: v.pipe(v.string(), v.minLength(1)),
      }),
    },
    defaultValues: {
      contactFirstname: company?.contactFirstname ?? '',
      contactLastname: company?.contactLastname ?? '',
      contactPhone: company?.contactPhone ?? '',
    },
    onSubmit: async ({ value }) => {
      await updateCompanyProfile(value)
      toast.success(t`Saved successfully`)
    },
  })

  return (
    <FormReadOnlyProvider value={!canWrite}>
      <div className={SETTINGS_STACK}>
        <SettingsSection
          tone={PROFILE_TONE}
          actions={
            canWrite ? (
              <companyForm.AppForm>
                <companyForm.SaveDiscardButtons />
              </companyForm.AppForm>
            ) : undefined
          }
          title={t`Company information`}
          description={t`Basic information about your company`}
          icon={BuildingIcon}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault()
              companyForm.handleSubmit()
            }}
            className={SETTINGS_FIELDS}
          >
            <companyForm.AppField
              name="name"
              children={(field) => (
                <field.TextField label={t`Company name`} orientation="horizontal" autoComplete="organization" />
              )}
            />
            <companyForm.AppField
              name="country"
              listeners={{ onChange: () => companyForm.setFieldValue('province', '') }}
              children={(field) => <field.NativeCountryField label={t`Country`} orientation="horizontal" />}
            />
            <companyForm.Subscribe selector={(state) => state.values.country}>
              {(country) => (
                <companyForm.AppField
                  name="province"
                  children={(field) => (
                    <field.NativeProvinceField label={t`Province / State`} orientation="horizontal" country={country} />
                  )}
                />
              )}
            </companyForm.Subscribe>
            <companyForm.AppField
              name="city"
              children={(field) => (
                <field.TextField label={t`City`} orientation="horizontal" autoComplete="address-level2" />
              )}
            />
            <companyForm.AppField
              name="cp"
              children={(field) => (
                <field.TextField label={t`Postal code`} orientation="horizontal" autoComplete="postal-code" />
              )}
            />
            <companyForm.AppField
              name="employees"
              children={(field) => (
                <field.SimpleSelectNumberField
                  label={t`Number of employees`}
                  options={lists.employees.map((label, value) => ({ value, label }))}
                  orientation="horizontal"
                />
              )}
            />
            <companyForm.AppField
              name="sector"
              children={(field) => <field.SectorField label={t`Sector`} orientation="horizontal" />}
            />
          </form>
        </SettingsSection>

        <SettingsSection
          tone={PROFILE_TONE}
          actions={
            canWrite ? (
              <contactForm.AppForm>
                <contactForm.SaveDiscardButtons />
              </contactForm.AppForm>
            ) : undefined
          }
          title={t`Contact information`}
          description={t`How we can reach you`}
          icon={PhoneIcon}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault()
              contactForm.handleSubmit()
            }}
            className={SETTINGS_FIELDS}
          >
            <contactForm.AppField
              name="contactFirstname"
              children={(field) => <field.TextField label={t`Contact first name`} orientation="horizontal" />}
            />
            <contactForm.AppField
              name="contactLastname"
              children={(field) => <field.TextField label={t`Contact last name`} orientation="horizontal" />}
            />
            <contactForm.AppField
              name="contactPhone"
              children={(field) => <field.TextField label={t`Contact phone number`} orientation="horizontal" />}
            />
          </form>
        </SettingsSection>
        <AiAppsAccess />
      </div>
    </FormReadOnlyProvider>
  )
}

// Read-only "My profile data" for an employee registered by a company.
// Mirrors the company's "Add employee" form (same sections, icons, labels, order) but static —
// the employee can view but not modify their data, and the Permissions section is not shown.
function ReadOnlyRow(props: { label: string; value: React.ReactNode }) {
  const { t } = useLingui()
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="text-muted-foreground text-sm">{props.label}</span>
      <span className="text-right text-sm font-medium">
        {props.value === '' || props.value == null ? (
          <span className="text-muted-foreground">{t`Not provided`}</span>
        ) : (
          props.value
        )}
      </span>
    </div>
  )
}

// Renders only while an employer is the ACTIVE context, so the session-resolved no-arg endpoints below
// all scope to that same employer — no companyId threading needed.
function EmployeeProfileReadOnly() {
  const { t } = useLingui()
  const user = useSessionUser()
  const navigate = useNavigate()
  const lists = useLists()
  // The employee's profile as stored by the active company (session-resolved by the no-arg form).
  const { data: profile } = useSuspenseQuery(Route.useRouteContext().myEmployeeProfileQueryOptions)

  // The employee's data exactly as the company stores it, with no fallback to the person's own account:
  // the company captures it (copied from the applicant) when adding the employee.
  // The teams they actually belong to within this employer, not every team the company owns.
  const { data: teams } = useSuspenseQuery(Route.useRouteContext().myTeamsQueryOptions)
  // Scoped to the ACTIVE employer, so an employee of several companies never sees the others mixed in.
  const { data: assignedJobsResult } = useSuspenseQuery(Route.useRouteContext().myAssignedJobsQueryOptions)

  // Value-shaped test gate: an incomplete-test employee must finish the questionnaires before their
  // employer-scoped profile means anything — send them to the next unfinished one (the same landing the
  // old TEST_INCOMPLETE error redirect produced, now explicit instead of a side effect of reading).
  if (assignedJobsResult.status !== 'ok') return <Navigate to={pickIncompleteTestPath(user)} />
  const assignedJobs = assignedJobsResult.list

  const sexLabel =
    profile?.sex === 'male' ? t`Man` : profile?.sex === 'female' ? t`Woman` : profile?.sex === 'neutral' ? t`Other` : ''
  const countryLabel = profile?.countryName ?? ''
  const provinceLabel = profile?.provinceName ?? ''
  const levelLabel = profile?.level != null ? lists.level[profile?.sex ?? 'neutral']?.[profile.level] : ''
  const functionLabel = profile?.function != null ? lists.function[profile.function] : ''
  const sectorLabel = profile?.sector != null ? lists.sector[profile.sector] : ''
  const educationLabel = profile?.educationalLevel != null ? lists.educationalLevel[profile.educationalLevel] : ''
  const teamNames = teams.flatMap((tm) => (tm.name ? [tm.name] : [])).join(', ')
  const puestoNames = assignedJobs.flatMap((j) => (j.name ? [j.name] : [])).join(', ')
  // Stored as YYYY-MM-DD; show day/month/year (Spanish) instead of the raw ISO order.
  const birthdateLabel = profile?.birthdate ? profile.birthdate.slice(0, 10).split('-').reverse().join('/') : ''

  return (
    <div className={SETTINGS_STACK}>
      <SettingsSection tone={PROFILE_TONE} title={t`Personal information`} icon={UserIcon}>
        <div className="flex flex-col">
          <div className="flex items-center justify-between gap-4 py-1.5">
            <span className="text-muted-foreground text-sm">{t`Photo`}</span>
            <Avatar
              size="xl"
              url={profile?.image}
              type={user?.type}
              name={`${profile?.firstname ?? ''} ${profile?.lastname ?? ''}`}
            />
          </div>
          <ReadOnlyRow label={t`Email`} value={profile?.email} />
          <ReadOnlyRow label={t`First name`} value={profile?.firstname} />
          <ReadOnlyRow label={t`Last name`} value={profile?.lastname} />
          <ReadOnlyRow label={t`Sex`} value={sexLabel} />
          <ReadOnlyRow label={t`Date of birth`} value={birthdateLabel} />
        </div>
      </SettingsSection>

      <SettingsSection tone={PROFILE_TONE} title={t`Organization`} icon={BuildingIcon}>
        <div className="flex flex-col">
          {/* "Puesto" = the position(s) the company assigned, not the free-text job title (which also
              renders as "Puesto" in Spanish and was the source of the spurious "No indicado"). */}
          <ReadOnlyRow label={t`Position`} value={puestoNames} />
          <ReadOnlyRow label={t`Teams`} value={teamNames} />
        </div>
      </SettingsSection>

      <SettingsSection tone={PROFILE_TONE} title={t`Professional details`} icon={BriefcaseIcon}>
        <div className="flex flex-col">
          <ReadOnlyRow label={t`Professional level`} value={levelLabel} />
          <ReadOnlyRow label={t`Function`} value={functionLabel} />
          <ReadOnlyRow label={t`Sector`} value={sectorLabel} />
          <ReadOnlyRow label={t`Studies`} value={educationLabel} />
          <ReadOnlyRow label={t`With people under charge?`} value={profile?.peopleInCharge ? t`Yes` : t`No`} />
        </div>
      </SettingsSection>

      <SettingsSection tone={PROFILE_TONE} title={t`Location`} icon={MapPinIcon}>
        <div className="flex flex-col">
          <ReadOnlyRow label={t`Country`} value={countryLabel} />
          <ReadOnlyRow label={t`Province / State`} value={provinceLabel} />
          <ReadOnlyRow label={t`City`} value={profile?.city} />
          <ReadOnlyRow label={t`Postal code`} value={profile?.cp} />
        </div>
      </SettingsSection>

      <div className="flex">
        <Button type="button" onClick={() => navigate({ to: '/report' })}>
          {t`Accept`}
        </Button>
      </div>
    </div>
  )
}

function ProfilePhoto(props: { onImageChange: (file: File) => void; changeLabel: string }) {
  const { t } = useLingui()
  const user = useSessionUser()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    // Reset input so re-selecting the same file triggers onChange
    event.target.value = ''

    const img = new Image()
    const objectUrl = URL.createObjectURL(file)
    img.addEventListener('load', () => {
      URL.revokeObjectURL(objectUrl)
      const w = img.width
      const h = img.height
      if (w < 256 || h < 256) {
        toast.error(t`Image too small`, {
          description: t`Image must be at least 256x256 pixels. Uploaded image is ${w}x${h} pixels.`,
        })
        return
      }
      setPendingFile(file)
      setIsEditing(true)
    })
    img.addEventListener('error', () => {
      URL.revokeObjectURL(objectUrl)
      toast.error(t`Invalid image`, { description: t`Failed to load image. Please try a different file.` })
    })
    img.src = objectUrl
  }

  return (
    <div className="flex flex-wrap items-center gap-4">
      <ButtonPrimitive
        type="button"
        onClick={() => fileInputRef.current?.click()}
        aria-label={props.changeLabel}
        className="group relative shrink-0 cursor-pointer overflow-hidden rounded-lg"
      >
        <Avatar size="4xl" url={user?.image} type={user?.type} name={user?.name} />
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 transition-colors group-hover:bg-black/50">
          <CameraIcon className="size-5 text-white opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
      </ButtonPrimitive>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="truncate text-sm font-medium">{user?.name || t`Your Name`}</p>
        <p className="text-muted-foreground truncate text-sm">{user?.email}</p>
      </div>
      <Button type="button" variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
        <CameraIcon className="size-4" />
        {props.changeLabel}
      </Button>
      <ImageEditor
        open={isEditing}
        file={pendingFile}
        aspectRatio={{ width: 128, height: 128 }}
        onSave={(croppedFile) => {
          props.onImageChange(croppedFile)
          setIsEditing(false)
          setPendingFile(null)
        }}
        onOpenChange={(open) => {
          if (!open) {
            setIsEditing(false)
            setPendingFile(null)
          }
        }}
      />
    </div>
  )
}

/** How you sign in, after the profile's own details: the email, the social accounts, the password. */
function SignInSections() {
  return (
    <>
      <ChangeEmailSection />
      <ConnectedAccountsSection />
      <PasswordSection />
    </>
  )
}

function ConnectedAccountsSection() {
  const { t } = useLingui()
  return (
    <SettingsSection
      tone={PROFILE_TONE}
      title={t`Connected accounts`}
      description={t`Sign in with Google or LinkedIn as well as your email and password`}
      icon={Link2Icon}
    >
      <ConnectedAccounts />
    </SettingsSection>
  )
}

/** There is no in-app password change: the reset link is the one way, so the section sends it to the login email. */
function PasswordSection() {
  const { t } = useLingui()
  const user = useSessionUser()
  const { mutateAsync: beginReset, isPending } = useMutation(trpc.auth.beginPasswordReset.mutationOptions())
  const send = async () => {
    const email = user?.email
    if (!email) return
    await beginReset(email)
    toast.success(t`We've sent a link to set a new password to ${email}.`)
  }
  return (
    <SettingsSection
      tone={PROFILE_TONE}
      title={t`Password`}
      description={t`Change it through a link sent to your login email`}
      icon={LockKeyholeIcon}
    >
      <div className="flex justify-end">
        <Button type="button" variant="secondary" onClick={send} disabled={isPending}>
          {isPending && <Spinner />}
          {t`Send reset link`}
        </Button>
      </div>
    </SettingsSection>
  )
}

function ChangeEmailSection() {
  const { t } = useLingui()
  const user = useSessionUser()
  const { mutateAsync: changeEmail } = useMutation(trpc.auth.changeEmail.mutationOptions())

  const currentEmail = user?.email?.toLowerCase() ?? ''

  const emailForm = useAppForm({
    defaultValues: {
      email: user?.email ?? '',
    },
    validators: {
      onChange: v.object({
        email: v.pipe(
          v.string(),
          v.email(),
          v.check((value) => value.toLowerCase() !== currentEmail, t`Email must be different from your current one`),
        ),
      }),
    },
    onSubmit: async ({ value }) => {
      try {
        await changeEmail({ email: value.email })
        toast.success(t`We've sent a verification link to your new email address. Please check your inbox.`)
      } catch {
        // Errors are surfaced (and translated via their machine code, e.g. EMAIL_IN_USE) by the global
        // tRPC error handler in main.tsx — no per-call message matching needed here.
      }
    },
  })

  return (
    <SettingsSection
      tone={PROFILE_TONE}
      actions={
        <emailForm.AppForm>
          <emailForm.SaveDiscardButtons />
        </emailForm.AppForm>
      }
      title={t`Email`}
      description={t`Your login email address`}
      icon={MailIcon}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          emailForm.handleSubmit()
        }}
        className={SETTINGS_FIELDS}
      >
        <emailForm.AppField
          name="email"
          children={(field) => <field.TextField label={t`Email`} orientation="horizontal" />}
        />
      </form>
    </SettingsSection>
  )
}

// Applicant Profile
// Fields, labels, order and mechanics mirror the registration flow
// (apps/.../applicants/applicant-form-section.tsx): About → Location → Career.
function UpdateApplicant() {
  const { t } = useLingui()
  const user = useSessionUser()
  const { mutateAsync: updateUser } = useMutation(trpc.auth.updateUser.mutationOptions())
  const lists = useLists()

  const personalForm = useAppForm({
    // Same validation as the registration "About + Location" steps so required fields show the "*"
    // marker and "Requerido" message, and a blank required field blocks save. `image` is handled
    // separately by the header avatar, so it's omitted from the schema here.
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: v.object({
        ...v.omit(personalInfoSchema, ['image']).entries,
        birthdate: birthdateSchemaKeeping(user?.birthdate),
      }),
    },
    defaultValues: {
      firstname: user?.firstname ?? '',
      lastname: user?.lastname ?? '',
      birthdate: user?.birthdate ?? '',
      sex: user?.sex,
      country: user?.country ?? '',
      province: user?.province ?? '',
      city: user?.city ?? '',
      cp: user?.cp ?? '',
    },
    onSubmit: async ({ value }) => {
      await updateUser(value)
      toast.success(t`Saved successfully`)
    },
  })

  const jobForm = useAppForm({
    // Same validation as the registration "Career" step (jobInfoSchema), which nests company/
    // employees under `work` and only requires them when `workedPreviously` is set — so toggling the
    // switch re-checks the conditionally-required fields, just like registration.
    validationLogic: revalidateLogic(),
    validators: { onDynamic: jobInfoSchema },
    defaultValues: {
      work: {
        // Transient UI flag (not persisted) driving the conditional fields, same as registration. Derived from
        // existing data: an already-employed professional has a company/employees on file.
        workedPreviously: user?.company != null || user?.employees != null,
        company: user?.company ?? null,
        employees: user?.employees ?? null,
      },
      job: user?.job ?? '',
      educationalLevel: user?.educationalLevel ?? null,
      level: user?.level ?? null,
      function: user?.function ?? null,
      sector: user?.sector ?? null,
    },
    onSubmit: async ({ value }) => {
      const { work, ...rest } = value
      await updateUser({
        ...rest,
        company: work.workedPreviously ? work.company?.trim() || null : null,
        employees: work.workedPreviously ? work.employees : null,
      })
      toast.success(t`Saved successfully`)
    },
  })

  const sexOptions = [
    { value: 'male', label: t`Man` },
    { value: 'female', label: t`Woman` },
    { value: 'neutral', label: t`Other` },
  ]

  const handleImageChange = async (file: File) => {
    await updateUser({ image: await fileToBase64(file) })
  }

  return (
    <div className={SETTINGS_STACK}>
      <SettingsSection
        tone={PROFILE_TONE}
        icon={UserRoundIcon}
        title={t`Profile`}
        description={t`Your photo and the name people see across D’Anchiano.`}
      >
        <ProfilePhoto onImageChange={handleImageChange} changeLabel={t`Change photo`} />
      </SettingsSection>

      <SettingsSection
        tone={PROFILE_TONE}
        actions={
          <personalForm.AppForm>
            <personalForm.SaveDiscardButtons />
          </personalForm.AppForm>
        }
        title={t`Personal information`}
        description={t`Helps us personalize your report`}
        icon={UserIcon}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            personalForm.handleSubmit()
          }}
          className={SETTINGS_FIELDS}
        >
          <personalForm.AppField
            name="firstname"
            children={(field) => <field.TextField label={t`First name`} orientation="horizontal" />}
          />
          <personalForm.AppField
            name="lastname"
            children={(field) => <field.TextField label={t`Last name`} orientation="horizontal" />}
          />
          <personalForm.AppField
            name="birthdate"
            children={(field) => <field.DateField label={t`Date of birth`} orientation="horizontal" />}
          />
          <personalForm.AppField
            name="sex"
            children={(field) => (
              <field.SimpleSelectField label={t`Sex`} options={sexOptions} orientation="horizontal" />
            )}
          />
          <personalForm.AppField
            name="country"
            listeners={{ onChange: () => personalForm.setFieldValue('province', '') }}
            children={(field) => <field.NativeCountryField label={t`Country`} orientation="horizontal" />}
          />
          <personalForm.Subscribe selector={(state) => state.values.country}>
            {(country) => (
              <personalForm.AppField
                name="province"
                children={(field) => (
                  <field.NativeProvinceField label={t`Province / State`} orientation="horizontal" country={country} />
                )}
              />
            )}
          </personalForm.Subscribe>
          <personalForm.AppField
            name="city"
            children={(field) => (
              <field.TextField label={t`City`} orientation="horizontal" autoComplete="address-level2" />
            )}
          />
          <personalForm.AppField
            name="cp"
            children={(field) => (
              <field.TextField label={t`Postal code`} orientation="horizontal" autoComplete="postal-code" />
            )}
          />
        </form>
      </SettingsSection>

      <SettingsSection
        tone={PROFILE_TONE}
        actions={
          <jobForm.AppForm>
            <jobForm.SaveDiscardButtons />
          </jobForm.AppForm>
        }
        title={t`Professional information`}
        description={t`Identify your profile in the market`}
        icon={BriefcaseIcon}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            jobForm.handleSubmit()
          }}
          className={SETTINGS_FIELDS}
        >
          <jobForm.Subscribe selector={(state) => state.values.work.workedPreviously}>
            {(workedPreviously) => (
              <>
                {/* Switch + company fields are one flex child so collapsing leaves a single
                    gap to the next field, not a doubled, empty one. */}
                <div className="flex flex-col">
                  <jobForm.AppField
                    name="work.workedPreviously"
                    listeners={{
                      onChange: ({ value: hasWorked }) => {
                        const level = jobForm.getFieldValue('level')
                        if (!hasWorked && level && level > 2) {
                          jobForm.resetField('level')
                        }
                      },
                    }}
                    children={(field) => <field.SwitchField label={t`I have worked before`} orientation="horizontal" />}
                  />

                  <Collapse open={workedPreviously}>
                    {/* pt-2 is the gap to the switch — kept inside so it collapses too */}
                    <div className="flex flex-col gap-2 pt-2">
                      <jobForm.AppField
                        name="work.company"
                        children={(field) => (
                          <field.TextField label={t`Company name`} orientation="horizontal" optional={false} />
                        )}
                      />
                      <jobForm.AppField
                        name="work.employees"
                        children={(field) => (
                          <field.SelectNumberField
                            label={t`Company employees`}
                            options={lists.employees.map((label, value) => ({ value, label }))}
                            orientation="horizontal"
                            optional={false}
                          />
                        )}
                      />
                    </div>
                  </Collapse>
                </div>

                <jobForm.AppField
                  name="job"
                  children={(field) => (
                    <field.TextField
                      label={workedPreviously ? t`Job title` : t`Preferred position`}
                      orientation="horizontal"
                    />
                  )}
                />

                <jobForm.AppField
                  name="educationalLevel"
                  children={(field) => (
                    <field.SimpleSelectNumberField
                      label={t`Studies`}
                      options={lists.educationalLevel.map((label, value) => ({ value, label }))}
                      orientation="horizontal"
                    />
                  )}
                />
                <jobForm.AppField
                  name="level"
                  children={(field) => (
                    <field.SimpleSelectNumberField
                      label={t`Professional level`}
                      options={
                        !workedPreviously
                          ? (lists.level[user?.sex ?? 'neutral'] ?? lists.level.neutral)
                              .slice(0, 2)
                              .map((label, value) => ({ value, label }))
                          : (lists.level[user?.sex ?? 'neutral'] ?? lists.level.neutral).map((label, value) => ({
                              value,
                              label,
                            }))
                      }
                      orientation="horizontal"
                    />
                  )}
                />
                <jobForm.AppField
                  name="function"
                  children={(field) => <field.FunctionField label={t`Function`} orientation="horizontal" />}
                />
                <jobForm.AppField
                  name="sector"
                  children={(field) => <field.SectorField label={t`Sector`} orientation="horizontal" />}
                />
              </>
            )}
          </jobForm.Subscribe>
        </form>
      </SettingsSection>

      <SignInSections />

      <SettingsSection
        tone={PROFILE_TONE}
        title={t`Danger zone`}
        description={t`Irreversible actions`}
        icon={AlertTriangleIcon}
        variant="danger"
      >
        <DeleteAccount />
      </SettingsSection>
    </div>
  )
}

// Principal administrator: the ONE membership carrying the `created_from_company_id == company_id` marker.
// The conmutador only runs employee → company now, so the marker means "who can come back INTO this
// account", not "who you switch into from here" — see employees.service.ts#setPrincipalAdmin.
// Owner-only surface (UpdateCompany renders only for the real company account).
function PrincipalAdminSection() {
  const { t } = useLingui()
  const { data: admins } = useSuspenseQuery(Route.useRouteContext().adminEmployeesQueryOptions)
  const { mutateAsync: setPrincipal } = useMutation(trpc.employees.setPrincipalAdmin.mutationOptions())
  const current = admins.find((a) => a.isPrincipal)?.id ?? null

  // Like every other Settings section: picking an administrator only stages it; Save hands the role over.
  const form = useAppForm({
    defaultValues: { employeeId: current == null ? '' : String(current) },
    onSubmit: async ({ value }) => {
      // A failure throws, so the section stays dirty; its message comes from the global tRPC error handler.
      await setPrincipal({ employeeId: Number(value.employeeId) })
      toast.success(t`Principal administrator updated`)
    },
  })

  return (
    <SettingsSection
      tone={PROFILE_TONE}
      title={t`Principal administrator`}
      description={t`The employee who can return to this company account from their own profile`}
      icon={StarIcon}
      actions={
        admins.length === 0 ? undefined : (
          <form.AppForm>
            <form.SaveDiscardButtons />
          </form.AppForm>
        )
      }
    >
      {admins.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t`Add an administrator employee first to designate a principal administrator.`}
        </p>
      ) : (
        <form
          className={SETTINGS_FIELDS}
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
        >
          <form.Field name="employeeId">
            {(field) => {
              const selected = admins.find((a) => String(a.id) === field.state.value)
              return (
                <Select
                  value={field.state.value}
                  onValueChange={(value) => value != null && value !== '' && field.handleChange(value)}
                >
                  {/* 46px: the 24px avatar gets the trigger's 10px left inset above and below it too. */}
                  <SelectTrigger className="w-full data-[size=default]:h-11.5">
                    {/* Base UI renders the raw VALUE (the employee id) unless the label is passed as children. */}
                    <SelectValue placeholder={t`Select an administrator`}>
                      {selected ? (
                        <span className="flex min-w-0 items-center gap-2">
                          <Avatar size="sm" type="employee" name={selected.name} url={selected.image} />
                          <span className="truncate">{selected.name ?? t`Unnamed administrator`}</span>
                        </span>
                      ) : undefined}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {/* SelectGroup carries the popup's inner padding (p-1), as in the form selects. */}
                    <SelectGroup>
                      {admins.map((admin) => (
                        <SelectItem key={admin.id} value={String(admin.id)}>
                          <span className="flex items-center gap-2">
                            <Avatar size="sm" type="employee" name={admin.name} url={admin.image} />
                            {admin.name ?? t`Unnamed administrator`}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              )
            }}
          </form.Field>
          <p className="text-muted-foreground text-xs">
            {t`Only this administrator can come back into the company account. Their role stays locked to Administrator, and only this company account can hand it to someone else.`}
          </p>
        </form>
      )}
    </SettingsSection>
  )
}

function UpdateCompany() {
  const { t } = useLingui()
  const user = useSessionUser()
  const { mutateAsync: updateUser } = useMutation(trpc.auth.updateUser.mutationOptions())
  const lists = useLists()

  const companyForm = useAppForm({
    // Every company field is required (drives the "*" markers and blocks save on empty) except the
    // postal code. Without validators the form saved blank required fields silently.
    validators: {
      onChange: v.object({
        name: v.pipe(v.string(), v.minLength(1)),
        country: v.pipe(v.string(), v.minLength(1)),
        province: v.pipe(v.string(), v.minLength(1)),
        city: v.pipe(v.string(), v.minLength(1)),
        cp: v.string(),
        employees: v.number(),
        sector: v.number(),
      }),
    },
    defaultValues: {
      name: user?.name ?? '',
      country: user?.country ?? '',
      province: user?.province ?? '',
      city: user?.city ?? '',
      cp: user?.cp ?? '',
      employees: user?.employees ?? null,
      sector: user?.sector ?? null,
    },
    onSubmit: async ({ value }) => {
      await updateUser({ ...value, firstname: value.name })
      toast.success(t`Saved successfully`)
    },
  })

  const contactForm = useAppForm({
    validators: {
      onChange: v.object({
        contactFirstname: v.pipe(v.string(), v.minLength(1)),
        contactLastname: v.pipe(v.string(), v.minLength(1)),
        contactPhone: v.pipe(v.string(), v.minLength(1)),
      }),
    },
    defaultValues: {
      contactFirstname: user?.contactFirstname ?? '',
      contactLastname: user?.contactLastname ?? '',
      contactPhone: user?.contactPhone ?? '',
    },
    onSubmit: async ({ value }) => {
      await updateUser(value)
      toast.success(t`Saved successfully`)
    },
  })

  const { mutateAsync: enableWelcome, isPending: enablingWelcome } = useMutation(
    trpc.auth.enableCompanyWelcome.mutationOptions(),
  )

  const handleImageChange = async (file: File) => {
    await updateUser({ image: await fileToBase64(file) })
  }

  const handleEnableWelcome = async () => {
    useAppStore.getState().resetWelcome()
    await enableWelcome()
  }

  return (
    <div className={SETTINGS_STACK}>
      <SettingsSection
        tone={PROFILE_TONE}
        icon={UserRoundIcon}
        title={t`Profile`}
        description={t`Your company’s logo and the name people see across D’Anchiano`}
      >
        <ProfilePhoto onImageChange={handleImageChange} changeLabel={t`Change logo`} />
      </SettingsSection>

      <SettingsSection
        tone={PROFILE_TONE}
        actions={
          <companyForm.AppForm>
            <companyForm.SaveDiscardButtons />
          </companyForm.AppForm>
        }
        title={t`Company information`}
        description={t`Basic information about your company`}
        icon={BuildingIcon}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            companyForm.handleSubmit()
          }}
          className={SETTINGS_FIELDS}
        >
          <companyForm.AppField
            name="name"
            children={(field) => (
              <field.TextField label={t`Company name`} orientation="horizontal" autoComplete="organization" />
            )}
          />
          <companyForm.AppField
            name="country"
            listeners={{ onChange: () => companyForm.setFieldValue('province', '') }}
            children={(field) => <field.NativeCountryField label={t`Country`} orientation="horizontal" />}
          />
          <companyForm.Subscribe selector={(state) => state.values.country}>
            {(country) => (
              <companyForm.AppField
                name="province"
                children={(field) => (
                  <field.NativeProvinceField label={t`Province / State`} orientation="horizontal" country={country} />
                )}
              />
            )}
          </companyForm.Subscribe>
          <companyForm.AppField
            name="city"
            children={(field) => (
              <field.TextField label={t`City`} orientation="horizontal" autoComplete="address-level2" />
            )}
          />
          <companyForm.AppField
            name="cp"
            children={(field) => (
              <field.TextField label={t`Postal code`} orientation="horizontal" autoComplete="postal-code" />
            )}
          />
          <companyForm.AppField
            name="employees"
            children={(field) => (
              <field.SimpleSelectNumberField
                label={t`Number of employees`}
                options={lists.employees.map((label, value) => ({ value, label }))}
                orientation="horizontal"
              />
            )}
          />
          <companyForm.AppField
            name="sector"
            children={(field) => <field.SectorField label={t`Sector`} orientation="horizontal" />}
          />
        </form>
      </SettingsSection>

      <SettingsSection
        tone={PROFILE_TONE}
        actions={
          <contactForm.AppForm>
            <contactForm.SaveDiscardButtons />
          </contactForm.AppForm>
        }
        title={t`Contact information`}
        description={t`How we can reach you`}
        icon={PhoneIcon}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            contactForm.handleSubmit()
          }}
          className={SETTINGS_FIELDS}
        >
          <contactForm.AppField
            name="contactFirstname"
            children={(field) => <field.TextField label={t`Contact first name`} orientation="horizontal" />}
          />
          <contactForm.AppField
            name="contactLastname"
            children={(field) => <field.TextField label={t`Contact last name`} orientation="horizontal" />}
          />
          <contactForm.AppField
            name="contactPhone"
            children={(field) => <field.TextField label={t`Contact phone number`} orientation="horizontal" />}
          />
        </form>
      </SettingsSection>

      <SignInSections />

      <SettingsSection
        tone={PROFILE_TONE}
        title={t`Welcome guide`}
        description={t`Show the onboarding guide again the next time you open the app`}
        icon={SparklesIcon}
      >
        <div className="flex items-center justify-between gap-4">
          <p className="text-muted-foreground text-sm">
            {t`Bring back the step-by-step guide that explains how to set up Jobs, Teams and Employees.`}
          </p>
          <Button type="button" variant="secondary" onClick={handleEnableWelcome} disabled={enablingWelcome}>
            {t`Show welcome guide`}
          </Button>
        </div>
      </SettingsSection>

      <AiAppsAccess />

      {/* Principal administrator (the one employee who can come back into this account) — loaded
          independently so the admin list never blocks the company forms above. */}
      <Suspense fallback={<SettingsSectionSkeleton rows={1} />}>
        <PrincipalAdminSection />
      </Suspense>

      <SettingsSection
        tone={PROFILE_TONE}
        title={t`Danger zone`}
        description={t`Irreversible actions`}
        icon={AlertTriangleIcon}
        variant="danger"
      >
        <DeleteAccount />
      </SettingsSection>
    </div>
  )
}

/**
 * Whether this company's people may connect external AI apps (MCP clients), shown only to who may change it: the
 * company account or an administrator acting for it. Off by default, since those apps receive real names and scores.
 */
function AiAppsAccess() {
  const user = useSessionUser()
  if (!user?.assistantEnabled) return null
  return (
    <Suspense fallback={<SettingsSectionSkeleton line />}>
      <AiAppsAccessSection />
    </Suspense>
  )
}

function AiAppsAccessSection() {
  const { t } = useLingui()
  const { data: access } = useSuspenseQuery(Route.useRouteContext().mcpCompanyAccessQueryOptions)
  const setAccess = useMutation(trpc.mcp.setCompanyAccess.mutationOptions())
  if (!access.canChange) return null
  return (
    <SettingsSection
      tone={PROFILE_TONE}
      title={t`AI apps`}
      description={t`Connections from Claude, ChatGPT and other AI chats`}
      icon={PlugIcon}
    >
      <label className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground text-sm">
          {t`Let this company’s people connect external AI apps to D’Anchiano. Those apps, and their AI providers, then receive the real names and scores of the people each person can see.`}
        </span>
        <Switch
          checked={access.enabled}
          disabled={setAccess.isPending}
          onCheckedChange={(enabled) => setAccess.mutate({ enabled })}
          aria-label={t`Allow external AI apps`}
        />
      </label>
    </SettingsSection>
  )
}
